import { Injectable } from '@angular/core';
import { updateRequestStatus } from '@ngneat/elf-requests';
import { catchError, map, take } from 'rxjs/operators';
import {
	upsertEntities,
	updateEntities,
	deleteEntities,
	getEntity,
	setActiveId,
	resetActiveId,
	getAllEntities
} from '@ngneat/elf-entities';
import { EMPTY, Observable, of, tap, throwError } from 'rxjs';

import { ProjectService } from '~core/services/project.service';
import { ArticleService } from '~core/services/article.service';
import { RegenerateAccepted } from '~core/services/bom-regenerate.service';
import { Part } from '~shared/types';
import { sortByUlid } from '~shared/helpers';

import { partStore } from './parts.store';
import { partsSelector } from './parts.selectors';
import { CreatePartDto, UpdatePartDto } from './parts.types';

@Injectable({
	providedIn: 'root',
})
export class PartsRepository {
	public parts$ = partsSelector.parts$;
	public selectedPart$ = partsSelector.selectedPart$;
	public partsLoading$ = partsSelector.partsLoading$;

	constructor(
		private readonly projectService: ProjectService,
		private readonly articleService: ArticleService,
	) {}

	public getPart$(partId: string): Observable<Part> {
		return this.projectService
			.getPart$(partId)
			.pipe(
				tap((part): void =>
					partStore.update(
						upsertEntities(part),
						updateRequestStatus('projects', 'success')
					)
				)
			);
	}

	public getProjectParts$(projectId: string): Observable<Part[]> {
		const existing = this.queryProjectParts(projectId);
		if (existing.length > 0) {
			return of(existing);
		}
		return this.getParts$(projectId);
	}

	public getParts$(projectId: string): Observable<Part[]> {
		partStore.update(updateRequestStatus('parts', 'pending'));
		return this.projectService
			.getParts$(projectId)
			.pipe(
				take(1),
				tap((parts) =>
					partStore.update(
						upsertEntities(parts),
						updateRequestStatus('parts', 'success')
					)
				)
			)
	}

	public queryParts(): Part[] {
		return sortByUlid(partStore.query(getAllEntities()))
	}

	// Generate a method to retrieve the active part from the repository
	public queryActivePartId(): string | undefined {
		return partStore.query((store) => store.activeId);
	}

	public queryActivePart(): Part | undefined {
		const activeId = this.queryActivePartId();
		if (!activeId) {
			return undefined;
		}
		return this.queryParts().find(p => p.id === activeId);
	}

	public queryPart(partId: string): Part | undefined {
		return partStore.query(getEntity(partId));
	}

	public queryProjectParts(projectId: string): Part[] {
		return this.queryParts().filter((part) => part.projectId === projectId);
	}

	public getPartHistory(partId: string): Observable<Part[]> {
		partStore.update(updateRequestStatus('parts', 'pending'));
		return this.projectService
			.getPartHistory(partId)
			.pipe(
				take(1)
			)
	}

	public createPart(part: CreatePartDto): Observable<Part> {
		partStore.update(updateRequestStatus('parts', 'pending'));
		return this.projectService
			.createPart(part)
			.pipe(
				take(1),
				tap((part) =>
					partStore.update(
						upsertEntities(part),
						updateRequestStatus('parts', 'success')
					)
				)
			);
	}

	public updatePart(partId: string, part: UpdatePartDto): Observable<Part> {
		// Shown at once, not when the backend answers; put back if it refuses.
		const previous = partStore.query(getEntity(partId));
		partStore.update(
			updateEntities(partId, part.name ? { name: part.name } : {}),
			updateRequestStatus('parts', 'pending'),
		);
		return this.projectService
			.updatePart(partId, part)
			.pipe(
				take(1),
				tap((part) => partStore.update(
					upsertEntities({
						...part,
						expectedDeliveryDate: part.expectedDeliveryDate || null
					}),
					updateRequestStatus('parts', 'success')
				)),
				catchError((error) => {
					if (previous) {
						partStore.update(upsertEntities(previous));
					}
					return throwError(() => error);
				}),
			)
	}

	public duplicatePart(partId: string): Observable<Part> {
		partStore.update(updateRequestStatus('parts', 'pending'));
		return this.projectService
			.duplicatePart(partId)
			.pipe(
				take(1),
				tap((part) =>{
					partStore.update(
					upsertEntities(part),
					updateRequestStatus('parts', 'success')
				)}),
				catchError(err => {
					partStore.update(updateRequestStatus('parts', 'error', err));
					return throwError(() => err);
				})
			)
	}

	public addCustomisationToPart(partId: string, customisationId: string): Observable<Part> {
		partStore.update(updateRequestStatus('add-customisation-to-part', 'pending'));
		return this.projectService
			.addCustomisationToPart(partId, customisationId)
			.pipe(
				take(1),
				tap((part) => partStore.update(
					upsertEntities(part),
					updateRequestStatus('add-customisation-to-part', 'success')
				))
			)
	}

	public deleteCustomisationFromPart(partId: string, customisationId: string): Observable<Part> {
		partStore.update(updateRequestStatus('delete-customisation-from-part', 'pending'));
		return this.projectService
			.deleteCustomisationFromPart(partId, customisationId)
			.pipe(
				take(1),
				tap((part) => partStore.update(
					upsertEntities(part),
					updateRequestStatus('add-customisation-to-part', 'success')
				))
			)
	}

	public updateStatus(partId: string, status: string): Observable<Part> {
		partStore.update(updateRequestStatus('parts', 'pending'));
		return this.projectService
			.updatePartStatus(partId, status)
			.pipe(
				take(1),
				tap((part) =>
					partStore.update(
						upsertEntities(part[0]),
						updateRequestStatus('parts', 'success')
					)
				)
			)
	}

	/**
	 * Takes the part out of the store at once and deletes it on the server. A failed delete
	 * puts it back, also as the one shown, and completes without a value: only a success emits.
	 */
	public deletePart$(partId: string): Observable<void> {
		const part = partStore.query(getEntity(partId));
		const wasActive = this.queryActivePartId() === partId;
		if (wasActive) {
			// A deleted part is no longer the one shown
			partStore.update(deleteEntities(partId), resetActiveId());
		} else {
			partStore.update(deleteEntities(partId));
		}
		return this.projectService
			.deletePart(partId)
			.pipe(
				take(1),
				map(() => undefined),
				catchError(() => {
					// The part comes back as it was, also as the one shown
					if (part && wasActive) {
						partStore.update(upsertEntities(part), setActiveId(partId));
					} else if (part) {
						partStore.update(upsertEntities(part));
					}
					return EMPTY;
				})
			);
	}

	public activatePart(partId: string): void {
		partStore.update(
			setActiveId(partId)
		)
	}

	public regenerateBomItems$(partId: string): Observable<RegenerateAccepted> {
		return this.articleService.regenerateBomItems(partId).pipe(take(1));
	}

	public reset(): void {
		partStore.reset()
	}
}
